import { type Route } from "./+types/privacy";

export const meta: Route.MetaFunction = () => {
  return [{ title: "Privacy Policy | shonaslang" }];
};

export default function PrivacyPage() {
  return (
    <div className="container py-16">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>

        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-muted-foreground mb-6">
            Effective date: January 2025
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              1. What we collect & why
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Data
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Purpose
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Legal basis
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      Email, handle, password hash
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Account creation, login
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Contract
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      IP address, browser headers
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Security, fraud prevention
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Legitimate interest
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      Votes, submissions, edits
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Core functionality
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Contract
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      Analytics events via Google Tag Manager
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Improve UX, measure traffic
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Consent
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              We do <strong>not</strong> sell personal data.
            </p>
            <p className="mt-4">
              <strong>Merch pre-orders.</strong> If you pre-order a jersey, we
              keep your name, email address, delivery address, phone number (if
              you give one), size and chosen word. We use them only to make and
              deliver your order and to handle refunds or questions about it,
              under our contract with you. Stripe processes your payment, and we
              may email you about your order through our email delivery service.
              We delete delivery addresses and phone numbers 90 days after the
              edition ships, and keep the rest of the order record for our
              accounts.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">2. Cookies</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Session cookie (ssid) keeps you logged in.</li>
              <li>
                Optional analytics cookies set by Google Analytics if you
                consent.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              3. Third-party processors
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Service
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Purpose
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Location
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2">
                      Email delivery service
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      Signup / verification emails
                    </td>
                    <td className="border border-gray-300 px-4 py-2">US</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">4. Data retention</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Account data – until you delete your account or after 3 years of
                inactivity.
              </li>
              <li>Log files – 30 days.</li>
              <li>
                Dictionary entries – retained indefinitely but decoupled from
                your email once you delete your account.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">5. Your rights</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access, rectify, delete or export your data.</li>
              <li>Object to processing for analytics.</li>
              <li>Withdraw consent at any time (settings → privacy).</li>
            </ul>
            <p className="mt-4">
              Email privacy@shonaslang.com to exercise these rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">6. Security</h2>
            <p>
              We use bcrypt hashing, HTTPS everywhere, and least-privilege
              access controls.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              7. International transfers
            </h2>
            <p>
              Data may be stored on servers outside your country. We rely on
              standard contractual clauses where required.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">8. Children</h2>
            <p>
              shonaslang is <strong>not directed at children under 13</strong>.
              We will delete data from a minor upon verified parental request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              9. Changes to this Policy
            </h2>
            <p>
              We'll notify users by email or banner for significant updates.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">10. Contact</h2>
            <p>privacy@shonaslang.com</p>
          </section>


        </div>
      </div>
    </div>
  );
}
